import { Box, Flex, Grid } from '@chakra-ui/react';
import * as dateFns from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  GetDashboardGraphData,
  GetMerchantNameList,
  GetRevenueGraphData,
  RevenueOverview,
  ErrorsOverview,
  ReoccurringUsers,
  GetSellerCommission,
} from 'reactRedux/thunks/dashboardThunk/dashboardThunk';
import { customStaticRanges, sideBarRoutes } from 'utils/StaticData';
import './custom-calendar.css';
import CountryRevenueOverview from './CountryRevenueOverview';
import NetRevenueGraph from './NetRevenueGraph';
import RecurringuserGraph from './RecurringuserGraph';
import RevenueOverViewGraph from './RevenueOverViewGraph';

export default function UserReports() {
  const addDays = dateFns.addDays;
  const dispatch = useDispatch();
  const { adminDetails } = useSelector((state) => state.authSlice);
  const {
    dashboardData,
    revenueGraphData,
    revenueOverviewData,
    merchantNameList,
    errorOverviewData,
    recurringUsers,
    sellerCommissionData,
    isLoading,
    isRevenueOverviewLoading,
    isRecurringLoading,
    isErrorLoading,
    isSellerCommissionLoading,
  } = useSelector((state) => state.DashboardSlice);
  // Prefer the currency the backend resolved for the selected region;
  // fall back to the admin's own region, then EUR for "all regions".
  const currency =
    revenueOverviewData?.currency ?? adminDetails?.region?.currency ?? 'EUR';
  const [showCalendar, setShowCalendar] = useState(false);
  const [tempMerchantNameList, setTempMerchantNameList] = useState([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const [dateRange, setDateRange] = React.useState([
    {
      endDate: new Date(),
      startDate: addDays(new Date(), -6), // Default 7 days range
      key: 'selection',
    },
  ]);
  const [calendarFilterTitle, setCalendarFilterTitle] = useState('Last 7 days');
  const [tempCalendarFilterTitle, setTempCalendarFilterTitle] =
    useState('Last 7 days');
  const [tempDateRange, setTempDateRange] = useState(dateRange);
  const [intervalValue, setIntervalValue] = useState(0);
  const [selectedMerchant, setSelectedMerchant] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [showAverage, setShowAverage] = useState(false);
  const [powerbankShowAvg, setPowerbankShowAvg] = useState(false);

  useEffect(() => {
    setTempMerchantNameList(merchantNameList);
  }, [merchantNameList]);

  useEffect(() => {
    if (
      [
        sideBarRoutes?.Admin,
        sideBarRoutes?.Dealer,
        sideBarRoutes?.Seller,
        sideBarRoutes?.Merchant,
      ].includes(adminDetails?.role)
    ) {
      dispatch(GetMerchantNameList());
    }

    // Call seller commission API only for seller role
    if (adminDetails?.role === sideBarRoutes?.Seller) {
      const startDate = dateFns.format(
        tempDateRange[0]?.startDate,
        'yyyy/MM/dd',
      );
      const endDate = dateFns.format(tempDateRange[0]?.endDate, 'yyyy/MM/dd');
      dispatch(GetSellerCommission({ start: startDate, end: endDate }));
    }

    dispatch(GetDashboardGraphData());
    dispatch(GetRevenueGraphData());

    const startDate = dateFns.format(tempDateRange[0]?.startDate, 'yyyy/MM/dd');
    const endDate = dateFns.format(tempDateRange[0]?.endDate, 'yyyy/MM/dd');

    dispatch(RevenueOverview({ start: startDate, end: endDate }));
    dispatch(ErrorsOverview({ start: startDate, end: endDate }));
    dispatch(ReoccurringUsers({ start: startDate, end: endDate }));
  }, [adminDetails, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const interval = setInterval(
      () => {
        dispatch(GetDashboardGraphData());
        dispatch(GetRevenueGraphData());
      },
      5 * 60 * 1000,
    ); // 5 minutes in milliseconds
    return () => clearInterval(interval); // Cleanup on unmount
  }, [dispatch]);

  useEffect(() => {
    const updateInterval = () => {
      const width = window.innerWidth;
      if (width > 1200) {
        setIntervalValue(1); // Show all labels on large screens
      } else if (width > 768) {
        setIntervalValue(2); // Skip every 2nd label on tablets
      } else {
        setIntervalValue(4); // Skip more labels on smaller screens
      }
    };

    updateInterval();
    window.addEventListener('resize', updateInterval);

    return () => window.removeEventListener('resize', updateInterval);
  }, []);

  const handleSelect = useCallback((ranges) => {
    const selectedRange = customStaticRanges.find(
      (range) =>
        range.range().startDate.getTime() ===
          ranges.selection.startDate.getTime() &&
        range.range().endDate.getTime() === ranges.selection.endDate.getTime(),
    );
    setTempCalendarFilterTitle(selectedRange?.label || 'Custom');
    setTempDateRange([ranges.selection]);
  }, []);

  const handleApply = useCallback(async () => {
    setDateRange(tempDateRange);
    const startDate = dateFns.format(tempDateRange[0]?.startDate, 'yyyy/MM/dd');
    const endDate = dateFns.format(tempDateRange[0]?.endDate, 'yyyy/MM/dd');

    const merchantIds =
      selectedMerchant && selectedMerchant.length > 0
        ? selectedMerchant.map((merchant) => merchant.merchantId).join(',')
        : '';

    setCalendarFilterTitle(tempCalendarFilterTitle);
    dispatch(
      RevenueOverview({
        start: startDate,
        end: endDate,
        merchantId: merchantIds,
        ...(selectedRegion && { regionId: selectedRegion }),
      }),
    );
    dispatch(
      ErrorsOverview({
        start: startDate,
        end: endDate,
        merchantId: merchantIds,
        ...(selectedRegion && { regionId: selectedRegion }),
      }),
    );
    dispatch(
      ReoccurringUsers({
        start: startDate,
        end: endDate,
        merchantId: merchantIds,
        ...(selectedRegion && { regionId: selectedRegion }),
      }),
    );

    if (adminDetails?.role === sideBarRoutes?.Seller) {
      dispatch(
        GetSellerCommission({
          start: startDate,
          end: endDate,
          ...(merchantIds && { merchantId: merchantIds }),
          ...(selectedRegion && { regionId: selectedRegion }),
        }),
      );
    }

    setShowCalendar(false);
  }, [
    tempDateRange,
    selectedMerchant,
    selectedRegion,
    tempCalendarFilterTitle,
    adminDetails?.role,
    dispatch,
  ]);

  const handleClear = useCallback(() => {
    setDateRange(tempDateRange);
    setShowCalendar(false);
  }, [tempDateRange]);

  const handleSelectChange = useCallback(
    (selectedMerchants) => {
      const regionId = selectedRegion;
      setSelectedMerchant(selectedMerchants);

      const startDate = dateFns.format(
        tempDateRange[0]?.startDate,
        'yyyy/MM/dd',
      );
      const endDate = dateFns.format(tempDateRange[0]?.endDate, 'yyyy/MM/dd');

      const merchantIds =
        selectedMerchants && selectedMerchants.length > 0
          ? selectedMerchants.map((merchant) => merchant.merchantId).join(',')
          : '';

      dispatch(
        RevenueOverview({
          start: startDate,
          end: endDate,
          merchantId: merchantIds,
          regionId,
        }),
      );
      dispatch(
        ErrorsOverview({
          start: startDate,
          end: endDate,
          merchantId: merchantIds,
          regionId,
        }),
      );
      dispatch(
        ReoccurringUsers({
          start: startDate,
          end: endDate,
          merchantId: merchantIds,
          regionId,
        }),
      );
    },
    [selectedRegion, tempDateRange, dispatch],
  );

  const handleRegionSelect = useCallback(
    (event) => {
      const regionId = event.target.value;

      const tempNameList = regionId
        ? merchantNameList.filter((item) => +item.regionId === +regionId)
        : merchantNameList;

      setSelectedMerchant([]);
      setSelectedRegion(regionId);
      setTempMerchantNameList(tempNameList);

      const startDate = dateFns.format(
        tempDateRange[0]?.startDate,
        'yyyy/MM/dd',
      );
      const endDate = dateFns.format(tempDateRange[0]?.endDate, 'yyyy/MM/dd');

      dispatch(
        RevenueOverview({
          start: startDate,
          end: endDate,
          regionId,
          merchantId: '',
        }),
      );
      dispatch(
        ErrorsOverview({
          start: startDate,
          end: endDate,
          regionId,
          merchantId: '',
        }),
      );
      dispatch(
        ReoccurringUsers({
          start: startDate,
          end: endDate,
          regionId,
          merchantId: '',
        }),
      );
    },
    [merchantNameList, tempDateRange, dispatch],
  );

  return (
    <>
      <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
        {/* Net Revanue */}
        <Grid
          templateColumns="repeat(12, 1fr)"
          mt="6"
          gap={{ base: '24px', md: '24px', lg: '24px' }}
        >
          <NetRevenueGraph
            intervalValue={intervalValue}
            revenueGraphData={revenueGraphData}
            currency={currency}
            dashboardData={dashboardData}
            isLoading={isLoading}
            adminDetails={adminDetails}
          />
        </Grid>

        {adminDetails?.role === sideBarRoutes?.Admin && (
          <Box
            mt="40px"
            bg="white"
            p="20px"
            pt="25px"
            borderRadius="20px"
            boxShadow="0px 0px 40px rgba(32, 57, 70, 0.06)"
          >
            <CountryRevenueOverview />
          </Box>
        )}

        <Box
          mt="40px"
          bg="white"
          p="20px"
          pt="25px"
          borderRadius="20px"
          boxShadow="0px 0px 40px rgba(32, 57, 70, 0.06)"
        >
          <RevenueOverViewGraph
            adminDetails={adminDetails}
            handleSelectChange={handleSelectChange}
            selectedMerchant={selectedMerchant}
            currency={currency}
            merchantNameList={tempMerchantNameList}
            revenueOverviewData={revenueOverviewData}
            sellerCommissionData={sellerCommissionData}
            isSellerCommissionLoading={isSellerCommissionLoading}
            isRevenueOverviewLoading={isRevenueOverviewLoading}
            isErrorLoading={isErrorLoading}
            showCalendar={showCalendar}
            setShowCalendar={setShowCalendar}
            tempDateRange={tempDateRange}
            setTempDateRange={setTempDateRange}
            dateRange={dateRange}
            setDateRange={setDateRange}
            calendarFilterTitle={calendarFilterTitle}
            handleApply={handleApply}
            handleClear={handleClear}
            handleSelect={handleSelect}
            selectedRegion={selectedRegion}
            handleRegionSelect={handleRegionSelect}
            setShowTooltip={setShowTooltip}
            showTooltip={showTooltip}
            recurringUsers={recurringUsers}
            isRecurringLoading={isRecurringLoading}
            showAverage={showAverage}
            setShowAverage={setShowAverage}
            powerbankShowAvg={powerbankShowAvg}
            setPowerbankShowAvg={setPowerbankShowAvg}
          />
        </Box>
        {adminDetails?.role !== sideBarRoutes?.Merchant && (
          <Flex
            mt={{ base: '24px', md: '40px' }}
            gap={{ base: '24px', lg: '24px', xl: '40px' }}
            flexWrap="wrap"
          >
            <RecurringuserGraph
              adminDetails={adminDetails}
              showTooltip={showTooltip}
              setShowTooltip={setShowTooltip}
              errorOverviewData={errorOverviewData}
              recurringUsers={recurringUsers}
              isErrorLoading={isErrorLoading}
            />
          </Flex>
        )}
      </Box>
    </>
  );
}
